import moment from "moment-timezone";
import {inject} from "aurelia-framework";
import {LocaleService} from "../../../i18n/locale-service";

@inject(LocaleService)
export class FilterValueToConditionsTransformer {
    constructor(localeService) {
        this.localeService = localeService;
    }
    /*public*/
    transform(value, property, type, options) {
        if (!value) {
            return value;
        }

        let object = {};

        const timezone = options?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

        switch (type) {
            case 'boolean':
                if (value === 'no') {
                    object[property] = {$ne: true};
                } else if (value === 'yes') {
                    object[property] = {$eq: true};
                }
                break;

            case 'phone':
                object[property] = {$phone: value};
                break;

            case 'text':
            case 'address':
                object[property] = {$match: value};
                break;

            case 'translatable':
                let language = this.localeService.contentLocale ?? "de";
                object[property+'.'+language] = { $match: value };
                break;

            case 'int-range':
                if (!value.from && !value.to) {
                    object = null;
                    break;
                }

                object[property] = {};

                if (value.from || value.from === 0) {
                    object[property]['$gte'] = parseInt(value.from);
                }

                if (value.to || value.to === 0) {
                    object[property]['$lte'] = parseInt(value.to);
                }
                break;

            case 'money-range':
                if (!value.from && !value.to) {
                    object = null;
                    break;
                }

                object[property + '.amount'] = {};

                if (value.from || value.from === 0.0) {
                    object[property + '.amount']['$gte'] = Math.round(parseFloat(value.from) * 100);
                }

                if (value.to || value.to === 0.0) {
                    object[property + '.amount']['$lte'] = Math.round(parseFloat(value.to) * 100);
                }
                break;

            case 'percentage-range':
                if (!value.from && !value.to) {
                    object = null;
                    break;
                }

                object[property] = {};

                if (value.from || value.from === 0.0) {
                    object[property]['$gte'] = parseFloat(value.from) / 100;
                }

                if (value.to || value.to === 0.0) {
                    object[property]['$lte'] = parseFloat(value.to) / 100;
                }
                break;

            case 'float-range':
                if (!value.from && !value.to) {
                    object = null;
                    break;
                }

                object[property] = {};

                if (value.from || value.from === 0.0) {
                    object[property]['$gte'] = parseFloat(value.from);
                }

                if (value.to || value.to === 0.0) {
                    object[property]['$lte'] = parseFloat(value.to);
                }
                break;

            case 'number':
                object[property] = {$eq: value};
                break;

            case 'tags':
                if (
                    (!value.include || value.include.length === 0) &&
                    (!value.exclude || value.exclude.length === 0)
                ) {
                    object = null;
                    break;
                }

                const tagMatchMode = value.mode ?? 'AND';

                if (value.include && value.include.length > 0) {
                    if (tagMatchMode === 'OR') {
                        object[property] = { $in: value.include };
                    } else {
                        object['$and'] = value.include.map(tag => ({
                            [property]: { $eq: tag }
                        }));
                    }
                }

                if (value.exclude && value.exclude.length > 0) {
                    if (!object[property]) {
                        object[property] = {};
                    }
                    object[property]['$nin'] = value.exclude;
                }

                break;

            case 'physical-size':
                if (!value.from && !value.to) {
                    object = null;
                    break;
                }

                object[property] = {};

                if (value.from) {
                    // We always calculate size in megabytes
                    object[property]['$gte'] = parseFloat(value.from) * 1024 * 1024;
                }

                if (value.to) {
                    object[property]['$lte'] = parseFloat(value.to) * 1024 * 1024;
                }

                break;

            case 'date-range':
                if (!value.fromDate && !value.toDate) {
                    object = null;
                    break;
                }

                object[property] = {};

                if (value.fromDate) {

                    const valueDate = timezone ? moment.tz(value.fromDate, timezone) : moment(value.fromDate);

                    object[property]['$gte'] = moment(valueDate).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ');
                }

                if (value.toDate) {

                    const valueDate = timezone ? moment.tz(value.toDate, timezone) : moment(value.toDate);

                    object[property]['$lte'] = moment(valueDate).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ');
                }
                break;

            case 'date':
            case 'dueDate':
            case 'age':

                const valueDate = timezone ? moment.tz(value, timezone) : moment(value);

                let start = moment(valueDate).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ');
                let end = moment(valueDate).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ');

                object[property] = {$gte: start, $lte: end};
                break;

            case 'choice':
                if (!value || value.length === 0) {
                    object = null;
                    break;
                }

                object[property] = {$in: value};
                break;

            default:
                object[property] = {$in: value};
        }

        return object;
    }
}
